import { isAdvice } from './materialCatalog'
import materialDisplayName from './materialDisplayName'

/**
 * Plan a single-material stock change for a household material entry.
 *
 * Pure: returns the op to apply, or `blockedBy` when a deduction would push
 * `QuantityInStock` below 0. The caller applies `op` to the Material doc.
 *
 * Met `allowNegative` (instelling 'Voorraad mag negatief worden') wordt er
 * nooit geblokkeerd; een tekort komt dan terug als `goesNegative` zodat de
 * aanroeper het eerst kan laten bevestigen. `blockedBy` en `goesNegative`
 * sluiten elkaar dus uit — de eerste weigert, de tweede waarschuwt.
 *
 * - `delta < 0` → deduct (adding a household material / increasing its quantity)
 * - `delta > 0` → restore (removing / decreasing) — never blocked
 *
 * A null/unknown material (orphan — deleted from the master list) yields an
 * empty plan so the caller silently no-ops on stock.
 *
 * A coachadvies does the same: het wordt niet ingekocht, dus er is geen
 * voorraad om af te boeken of terug te geven. Zonder deze uitzondering leest
 * zijn QuantityInStock van 0 als 'op' en weigert elke registratie.
 *
 * @param {{ID: string, Title?: string, Kind?: string, QuantityInStock?: number}|null} material
 * @param {number} delta
 * @param {{allowNegative?: boolean}} [options]
 * @returns {{
 *   op?: {MaterialID: string, delta: number},
 *   blockedBy?: {MaterialID: string, Title: string, Required: number, Available: number},
 *   goesNegative?: {MaterialID: string, Title: string, Required: number, Available: number, Resulting: number}
 * }}
 */
const planMaterialStockChange = (material, delta, { allowNegative = false } = {}) => {
    if (!material || !material.ID) return {}
    if (isAdvice(material)) return {}

    const amount = Number(delta) || 0
    if (amount === 0) return {}

    const available = Number(material.QuantityInStock) || 0

    const op = { MaterialID: material.ID, delta: amount }

    if (amount < 0) {
        const required = -amount
        if (required > available) {
            const shortage = {
                MaterialID: material.ID,
                Title: materialDisplayName(material),
                Required: required,
                Available: available,
            }

            if (!allowNegative) return { blockedBy: shortage }

            return { op, goesNegative: { ...shortage, Resulting: available + amount } }
        }
    }

    return { op }
}

export default planMaterialStockChange
